import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableRow, Paper,
  Typography, CircularProgress, Box, TableHead, IconButton, Collapse,
  Divider, Dialog, DialogContent, Button, Snackbar, Alert, Tooltip,
  TextField, MenuItem, InputAdornment, Stack, Chip, Avatar, Grid,
  FormControl, InputLabel, Select,
} from '@mui/material';
import {
  AccountCircle, AddCircle, KeyboardArrowDown, KeyboardArrowUp,
  Subscriptions, Refresh as RefreshIcon, LocalOffer, Discount, Delete,
  Search as SearchIcon, Clear as ClearIcon, AdminPanelSettings,
  PhoneIphone, Email, Badge as BadgeIcon, PersonAdd,
} from '@mui/icons-material';
import AddSucriptionForm from '../subscription/AddSucriptionForm';
import LoginRegister from '../register/LoginRegister';
import authService from '../Auth/AuthService';
import PageShell from '../sales/common/PageShell';
import StatCard from '../sales/common/StatCard';
import EmptyState from '../sales/common/EmptyState';

// ---------- Helpers ----------

const colorFor = (name = '') => {
  const palette = ['#2563eb', '#7c3aed', '#db2777', '#ea580c', '#059669', '#0891b2', '#4f46e5'];
  let sum = 0;
  for (let i = 0; i < String(name).length; i++) sum += String(name).charCodeAt(i);
  return palette[sum % palette.length];
};

const yesNoChip = (value, yesColor = 'success', noColor = 'default') => (
  <Chip
    label={value ? 'Yes' : 'No'}
    size="small"
    color={value ? yesColor : noColor}
    variant={value ? 'filled' : 'outlined'}
    sx={{ fontWeight: 600 }}
  />
);

const statusChip = (isActive) => (
  <Chip
    label={isActive ? 'Active' : 'Inactive'}
    size="small"
    color={isActive ? 'success' : 'default'}
    variant={isActive ? 'filled' : 'outlined'}
    sx={{ fontWeight: 600 }}
  />
);

const SectionTitle = ({ icon, title, right }) => (
  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
    <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, '& svg': { fontSize: 16 } }}>
      {icon}
    </Avatar>
    <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
      {title}
    </Typography>
    {right}
  </Stack>
);

// ---------- Page ----------

const LoginDetailView = ({ user } = {}) => {
  const [loginData, setLoginData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openRow, setOpenRow] = useState({});
  const [open, setOpen] = useState(false);
  const [openLogin, setOpenLogin] = useState(false);
  const [selectedLoginId, setSelectedLoginId] = useState(null);
  const [preSelectedCouponCode, setPreSelectedCouponCode] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const fetchedRef = useRef(false);

  // Search & filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    roleType: '',
    loginType: '',
    status: '',
    mobileVerify: '',
    emailVerify: '',
  });

  // ---------- Data ----------
  const loadData = async () => {
    try {
      setLoading(true);
      const url = authService.pathUrl('GET', '/auth/login');
      const res = await authService.getAuth(url);
      setLoginData(Array.isArray(res) ? res : []);
    } catch (e) {
      console.error('Error fetching logins:', e);
      setSnackbar({ open: true, message: 'Failed to load login records', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    loadData();
  }, []);

  // ---------- Actions ----------
  const toggleRow = (id) => setOpenRow((prev) => ({ ...prev, [id]: !prev[id] }));

  const openAddSubscription = (login, coupon) => {
    setSelectedLoginId({ id: login.id, paidby: user });
    setPreSelectedCouponCode(coupon);
    setOpen(true);
  };

  const isCouponValid = (userCoupon) => {
    if (!userCoupon) return false;
    if (userCoupon.isUsed) return false;
    const today = new Date().toISOString().split('T')[0];
    const endDate = userCoupon.endDate || userCoupon.coupon?.endDate;
    if (endDate && endDate < today) return false;
    const startDate = userCoupon.startDate || userCoupon.coupon?.startDate;
    if (startDate && startDate > today) return false;
    return true;
  };

  const showSnackbar = (message, severity = 'info') =>
    setSnackbar({ open: true, message, severity });

  const handleRefreshAll = () => {
    loadData();
    showSnackbar('Refreshed successfully', 'success');
  };

  const mobileVerify = async (login) => {
    try {
      const data = { ...login, isMobileVerify: true };
      const url = authService.pathUrl('PUT', '/auth/admin/verify-otp/' + login.id);
      await authService.putAuth(url, data);
      showSnackbar('Mobile verified', 'success');
      loadData();
    } catch (e) {
      console.error('Error verifying mobile:', e);
      showSnackbar('Failed to verify mobile', 'error');
    }
  };

  const deleteLogin = async (loginId) => {
    try {
      const url = authService.pathUrl('DELETE', '/auth/' + loginId);
      await authService.deleteAuth(url);
      showSnackbar('Login deleted', 'success');
      loadData();
    } catch (e) {
      console.error('Error deleting login:', e);
      showSnackbar('Failed to delete login', 'error');
    }
  };

  // ---------- Derived options ----------
  const roleTypeOptions = useMemo(
    () => [...new Set((loginData || []).map((l) => l.roleType).filter(Boolean))],
    [loginData]
  );
  const loginTypeOptions = useMemo(
    () => [...new Set((loginData || []).map((l) => l.loginType).filter(Boolean))],
    [loginData]
  );

  // ---------- Filtering ----------
  const filteredData = useMemo(() => {
    if (!Array.isArray(loginData)) return [];
    const term = searchTerm.trim().toLowerCase();

    return loginData.filter((login) => {
      if (term) {
        const haystack = [
          login.id,
          login.email,
          login.mobile,
          login.roleType,
          login.loginType,
          login.masterCode,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(term)) return false;
      }

      if (filters.roleType && login.roleType !== filters.roleType) return false;
      if (filters.loginType && login.loginType !== filters.loginType) return false;

      if (filters.status === 'active' && !login.isActive) return false;
      if (filters.status === 'inactive' && login.isActive) return false;

      if (filters.mobileVerify === 'yes' && !login.isMobileVerify) return false;
      if (filters.mobileVerify === 'no' && login.isMobileVerify) return false;

      if (filters.emailVerify === 'yes' && !login.isEmailVerify) return false;
      if (filters.emailVerify === 'no' && login.isEmailVerify) return false;

      return true;
    });
  }, [loginData, searchTerm, filters]);

  // ---------- Stats ----------
  const stats = useMemo(() => {
    const total = loginData.length;
    const active = loginData.filter((l) => l.isActive).length;
    const mobileVerified = loginData.filter((l) => l.isMobileVerify).length;
    const emailVerified = loginData.filter((l) => l.isEmailVerify).length;
    return { total, active, mobileVerified, emailVerified };
  }, [loginData]);

  // ---------- Handlers ----------
  const handleFilterChange = (field) => (e) =>
    setFilters((prev) => ({ ...prev, [field]: e.target.value }));

  const clearFilters = () => {
    setSearchTerm('');
    setFilters({
      roleType: 'ADMIN',
      loginType: '',
      status: '',
      mobileVerify: '',
      emailVerify: '',
    });
  };

  const hasActiveFilters =
    searchTerm || Object.values(filters).some((v) => v !== '');

  // ---------- Loading ----------
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PageShell
      icon={<AdminPanelSettings />}
      title="Login Details"
      subtitle="All registered users, subscriptions and referral coupons"
      breadcrumbs={[{ label: 'Dashboard', to: '/' }, { label: 'Logins' }]}
      actions={
        <>
          <Tooltip title="Refresh">
            <IconButton
              onClick={handleRefreshAll}
              disabled={loading}
              sx={{
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.5)',
                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={() => setOpenLogin(true)}
            sx={{
              bgcolor: '#fff',
              color: '#1e3a8a',
              fontWeight: 700,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
            }}
          >
            Add User
          </Button>
        </>
      }
    >
      {/* Stats */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          mb: 3,
          gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
        }}
      >
        <StatCard label="Total Logins" value={stats.total} icon={<AccountCircle />} color="#2563eb" />
        <StatCard label="Active" value={stats.active} icon={<BadgeIcon />} color="#059669" />
        <StatCard label="Mobile Verified" value={stats.mobileVerified} icon={<PhoneIphone />} color="#7c3aed" />
        <StatCard label="Email Verified" value={stats.emailVerified} icon={<Email />} color="#0891b2" />
      </Box>

      {/* Filter bar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2.5,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search by ID, email, mobile, role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchTerm('')} edge="end">
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }
              }}
            />
          </Grid>

          <Grid item xs={6} sm={3} size={1.5}>
            <FormControl size="small" fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={filters.roleType}
                onChange={handleFilterChange('roleType')}
                label="Role"
              >
                <MenuItem value="">All</MenuItem>
                {roleTypeOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={3} size={1.5}>
            <FormControl size="small" fullWidth>
              <InputLabel>Login</InputLabel>
              <Select
                value={filters.loginType}
                onChange={handleFilterChange('loginType')}
                label="Login"
              >
                <MenuItem value="">All</MenuItem>
                {loginTypeOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={3} size={1.5}>
            <FormControl size="small" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={handleFilterChange('status')}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={3} size={1.5}>
            <FormControl size="small" fullWidth>
              <InputLabel>Mobile</InputLabel>
              <Select
                value={filters.mobileVerify}
                onChange={handleFilterChange('mobileVerify')}
                label="Mobile"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="yes">Verified</MenuItem>
                <MenuItem value="no">Not verified</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={3} size={1.5}>
            <FormControl size="small" fullWidth>
              <InputLabel>Email</InputLabel>
              <Select
                value={filters.emailVerify}
                onChange={handleFilterChange('emailVerify')}
                label="Email"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="yes">Verified</MenuItem>
                <MenuItem value="no">Not verified</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {hasActiveFilters && (
            <Grid item xs={6} sm="auto">
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<ClearIcon />}
                onClick={clearFilters}
              >
                Clear
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Results count */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Showing <strong>{filteredData.length}</strong> of{' '}
        <strong>{loginData.length}</strong> logins
        {hasActiveFilters && ' (filtered)'}
      </Typography>

      {/* Table / Empty */}
      {filteredData.length === 0 ? (
        <Paper elevation={0} sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
          <EmptyState
            icon={<AccountCircle />}
            title={loginData.length === 0 ? 'No logins yet' : 'No matches'}
            subtitle={
              loginData.length === 0
                ? 'Registered users will appear here.'
                : 'Try adjusting your search or filters.'
            }
            actionLabel={hasActiveFilters ? 'Clear Filters' : undefined}
            onAction={clearFilters}
          />
        </Paper>
      ) : (
        <Paper
          elevation={0}
          sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}
        >
          <TableContainer>
            <Table sx={{ minWidth: 1100 }}>
              <TableHead>
                <TableRow
                  sx={{
                    '& th': {
                      bgcolor: 'grey.50',
                      fontWeight: 700,
                      fontSize: 12,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      color: 'text.secondary',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                    },
                  }}
                >
                  <TableCell width={48} />
                  <TableCell>ID</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>Role / Login</TableCell>
                  <TableCell>Master Code</TableCell>
                  <TableCell align="center">Mobile</TableCell>
                  <TableCell align="center">Email</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.map((login) => {
                  const expandable =
                    (login.subscipe?.length || 0) > 0 ||
                    (login.userCoupons?.length || 0) > 0;
                  const isOpen = !!openRow[login.id];
                  const avatarColor = colorFor(login.email || login.mobile || String(login.id));
                  const initial = (login.email || login.mobile || '?').charAt(0).toUpperCase();

                  return (
                    <React.Fragment key={login.id}>
                      <TableRow
                        hover
                        sx={{ '& > *': { borderBottom: isOpen ? 'none' : undefined } }}
                      >
                        <TableCell>
                          {expandable && (
                            <IconButton size="small" onClick={() => toggleRow(login.id)}>
                              {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                            </IconButton>
                          )}
                        </TableCell>

                        <TableCell>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={600}
                          >
                            #{String(login.id).padStart(5, '0')}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar
                              sx={{
                                bgcolor: avatarColor,
                                width: 36,
                                height: 36,
                                fontWeight: 700,
                                fontSize: 14,
                              }}
                            >
                              {initial}
                            </Avatar>
                            <Typography variant="body2" fontWeight={700} noWrap>
                              {login.email || '—'}
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2">{login.mobile || '—'}</Typography>
                        </TableCell>

                        <TableCell>
                          <Stack spacing={0.5}>
                            <Chip
                              label={login.roleType || '—'}
                              size="small"
                              sx={{
                                bgcolor: '#dbeafe',
                                color: '#1e40af',
                                fontWeight: 700,
                                border: 'none',
                                alignSelf: 'flex-start',
                              }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {login.loginType || '—'}
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                          >
                            {login.masterCode || '—'}
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Tooltip
                            title={
                              login.isMobileVerify
                                ? 'Mobile verified'
                                : 'Click to verify mobile'
                            }
                          >
                            <Box
                              onClick={() => !login.isMobileVerify && mobileVerify(login)}
                              sx={{
                                display: 'inline-flex',
                                cursor: login.isMobileVerify ? 'default' : 'pointer',
                              }}
                            >
                              {yesNoChip(login.isMobileVerify)}
                            </Box>
                          </Tooltip>
                        </TableCell>

                        <TableCell align="center">{yesNoChip(login.isEmailVerify)}</TableCell>

                        <TableCell align="center">{statusChip(login.isActive)}</TableCell>

                        <TableCell align="right">
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => deleteLogin(login.id)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>

                      {/* Expandable detail row */}
                      <TableRow>
                        <TableCell colSpan={10} sx={{ p: 0, borderBottom: isOpen ? undefined : 'none' }}>
                          <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <Box
                              sx={{
                                p: 3,
                                bgcolor: 'grey.50',
                                borderTop: '1px solid',
                                borderColor: 'divider',
                              }}
                            >
                              {/* Subscriptions */}
                              <SectionTitle
                                icon={<Subscriptions />}
                                title="Subscriptions"
                                right={
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<AddCircle />}
                                    onClick={() => openAddSubscription(login)}
                                  >
                                    Add Subscription
                                  </Button>
                                }
                              />
                              {login.subscipe?.length > 0 ? (
                                <Paper
                                  elevation={0}
                                  sx={{
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    overflow: 'hidden',
                                    mb: 3,
                                  }}
                                >
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow
                                        sx={{
                                          '& th': {
                                            bgcolor: 'grey.100',
                                            fontWeight: 700,
                                            fontSize: 11,
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.4,
                                            color: 'text.secondary',
                                          },
                                        }}
                                      >
                                        <TableCell>ID</TableCell>
                                        <TableCell>Plan</TableCell>
                                        <TableCell align="right">Price</TableCell>
                                        <TableCell align="right">Days</TableCell>
                                        <TableCell>Coupon</TableCell>
                                        <TableCell>Start</TableCell>
                                        <TableCell>End</TableCell>
                                        <TableCell align="center">Active</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {login.subscipe.map((sub) => (
                                        <TableRow key={sub.id} hover>
                                          <TableCell>
                                            <Typography variant="caption" fontWeight={600}>
                                              #{sub.id}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="body2" fontWeight={600}>
                                              {sub.subscriptionPlan?.name || '—'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align="right">
                                            <Typography variant="body2">
                                              ₹{sub.subscriptionPlan?.price ?? '—'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align="right">
                                            <Typography variant="body2">
                                              {sub.subscriptionPlan?.durationInDays ?? '—'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            {sub.couponCode ? (
                                              <Chip
                                                label={sub.couponCode}
                                                size="small"
                                                sx={{
                                                  fontFamily: 'monospace',
                                                  fontWeight: 700,
                                                  bgcolor: '#dbeafe',
                                                  color: '#1e40af',
                                                  border: 'none',
                                                }}
                                              />
                                            ) : (
                                              <Typography variant="caption" color="text.disabled">
                                                —
                                              </Typography>
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="caption">
                                              {sub.startDate || '—'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="caption">
                                              {sub.endDate || '—'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align="center">
                                            {yesNoChip(sub.isActive)}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Paper>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 3, fontStyle: 'italic' }}
                                >
                                  No subscriptions recorded.
                                </Typography>
                              )}

                              {/* Coupons */}
                              <SectionTitle
                                icon={<LocalOffer />}
                                title="Referral Coupons"
                              />
                              {login.userCoupons?.length > 0 ? (
                                <Paper
                                  elevation={0}
                                  sx={{
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    overflow: 'hidden',
                                  }}
                                >
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow
                                        sx={{
                                          '& th': {
                                            bgcolor: 'grey.100',
                                            fontWeight: 700,
                                            fontSize: 11,
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.4,
                                            color: 'text.secondary',
                                          },
                                        }}
                                      >
                                        <TableCell>Code</TableCell>
                                        <TableCell align="right">Discount</TableCell>
                                        <TableCell align="right">Amount</TableCell>
                                        <TableCell>Valid From</TableCell>
                                        <TableCell>Valid To</TableCell>
                                        <TableCell align="center">Used</TableCell>
                                        <TableCell>Used At</TableCell>
                                        <TableCell align="right">Action</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {login.userCoupons.map((uc) => {
                                        const valid = isCouponValid(uc);
                                        const couponCode = uc.coupon?.code;
                                        return (
                                          <TableRow key={uc.id} hover>
                                            <TableCell>
                                              <Chip
                                                label={couponCode || '—'}
                                                size="small"
                                                sx={{
                                                  fontFamily: 'monospace',
                                                  fontWeight: 700,
                                                  bgcolor: valid ? '#dcfce7' : '#f1f5f9',
                                                  color: valid ? '#166534' : '#475569',
                                                  border: 'none',
                                                }}
                                              />
                                            </TableCell>
                                            <TableCell align="right">
                                              <Typography variant="body2">
                                                {uc.coupon?.discountPercentage != null
                                                  ? `${uc.coupon.discountPercentage}%`
                                                  : '—'}
                                              </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                              <Typography variant="body2">
                                                {uc.coupon?.discountAmount != null
                                                  ? `₹${uc.coupon.discountAmount}`
                                                  : '—'}
                                              </Typography>
                                            </TableCell>
                                            <TableCell>
                                              <Typography variant="caption">
                                                {uc.startDate || uc.coupon?.startDate || '—'}
                                              </Typography>
                                            </TableCell>
                                            <TableCell>
                                              <Typography variant="caption">
                                                {uc.endDate || uc.coupon?.endDate || '—'}
                                              </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                              {yesNoChip(uc.isUsed, 'error', 'success')}
                                            </TableCell>
                                            <TableCell>
                                              <Typography variant="caption" color="text.secondary">
                                                {uc.usedAt || '—'}
                                              </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                              {valid && couponCode ? (
                                                <Button
                                                  size="small"
                                                  variant="contained"
                                                  startIcon={<Discount fontSize="small" />}
                                                  onClick={() => openAddSubscription(login, uc)}
                                                >
                                                  Apply
                                                </Button>
                                              ) : (
                                                <Typography variant="caption" color="text.disabled">
                                                  —
                                                </Typography>
                                              )}
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                </Paper>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ fontStyle: 'italic' }}
                                >
                                  No coupons assigned.
                                </Typography>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Subscription dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5, overflow: 'hidden' } }}
      >
        <DialogContent sx={{ p: 0 }}>
          {open && (
            <AddSucriptionForm
              loginId={selectedLoginId}
              coupon={preSelectedCouponCode}
              onSuccess={() => {
                setOpen(false);
                loadData();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Register dialog */}
      <Dialog
        open={openLogin}
        onClose={() => setOpenLogin(false)}
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 2.5, overflow: 'hidden' } }}
      >
        <DialogContent sx={{ py: 0 }}>
          {openLogin && (
            <LoginRegister
              onSuccess={() => {
                setOpenLogin(false);
                loadData();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageShell>
  );
};

export default LoginDetailView;